export class FetchError extends Error {
	responseCode: number;
	errorType?: string;
	response: Response;
	responseJson?: any;

	constructor(
		action: string,
		responseCode: number,
		response: Response,
		responseJson: any | undefined = undefined,
		errorType: string | undefined = undefined,
	) {
		if (responseJson?.message) {
			super(responseJson.message);
		} else {
			super(`Received error response during action ${action}: ${response.statusText}`);
		}
		this.name = 'FetchError';
		this.responseCode = responseCode;
		this.response = response;
		this.responseJson = responseJson;
		this.errorType = errorType;
	}
}

export enum RespCodes {
	NO_CONTENT = 204,
	NOT_MODIFIED = 304,
	BAD_REQUESTS = 400,
	CLIENT_CLOSED_REQUEST = 499,
}

export interface RecaptchaOptions {
	recaptchaToken?: string;
	recaptchaAction?: string;
}

const handleFailedResponse = async <T>(response: Response, action: string): Promise<T> => {
	let failedResponseJson: any | undefined;
	try {
		failedResponseJson = await response.json();
	} catch {}

	throw new FetchError(action, response.status, response, failedResponseJson);
};

const handleErrorResponse = (err: any, message: string) => {
	if (err instanceof FetchError) {
		throw err;
	} else if (err instanceof Error) {
		err.message = message + `: ${err.message}`;
	}
	throw err;
};

export const postRequest = async <T>(url: string, action: string, data: Record<string, any>) => {
	try {
		const response = await fetch(url, {
			credentials: 'include',
			method: 'POST',
			...requestTemplate(data),
		});
		if (response.ok) {
			return response;
		} else {
			return handleFailedResponse(response, action);
		}
	} catch (err) {
		const message = `Error while executing post request for ${action}`;
		return handleErrorResponse(err, message);
	}
};

export const fetchJson = async <T>(url: string, action: string, init?: RequestInit): Promise<T> => {
	try {
		const response = await fetch(url, { credentials: 'include', ...init });
		if (response.status === RespCodes.NOT_MODIFIED) {
			return response.json();
		}
		if (response.ok) {
			// @ts-ignore - TS2322 TypeScript 5.9.2 upgrade
			return response.status === RespCodes.NO_CONTENT ? undefined : response.json();
		} else {
			return handleFailedResponse(response, action);
		}
	} catch (err) {
		const message = `Error while executing fetch for ${action}`;
		return handleErrorResponse(err, message);
	}
};

const requestTemplate = (data: Record<string, any>, recaptchaOptions?: RecaptchaOptions) => ({
	headers: {
		'Content-Type': 'application/json',
		...(recaptchaOptions?.recaptchaToken
			? { 'G-Recaptcha-Token': recaptchaOptions.recaptchaToken }
			: {}),
		...(recaptchaOptions?.recaptchaAction
			? { 'Recaptcha-Action': recaptchaOptions.recaptchaAction }
			: {}),
	},
	body: JSON.stringify(data),
});

export const postJson = <T>(
	url: string,
	action: string,
	data: Record<string, any>,
	recatpchaOptions?: RecaptchaOptions,
) =>
	fetchJson<T>(url, action, {
		method: 'POST',
		...requestTemplate(data, recatpchaOptions),
	});

export const putJson = <T>(url: string, action: string, data: Record<string, any>) =>
	fetchJson<T>(url, action, {
		method: 'PUT',
		...requestTemplate(data),
	});

const isUnauthorisedError = (error: FetchError) => {
	return error.responseCode === 401;
};

export const isSLOFailure = (error: unknown): boolean => {
	if (error instanceof FetchError) {
		return !isUnauthorisedError(error);
	}
	return true;
};

export const getErrorDetails = (err: unknown) => ({
	code: err instanceof Error ? err.name : 'UNKNOWN',
	message: err instanceof Error ? err.message : String(err),
	status: err instanceof FetchError ? err.responseCode : undefined,
});
